// Reader for the self-describing per-hex binary files written by tools/bake (`*.grid.bin`).
//
// Layout (little-endian):
//
//   offset 0   magic      4 bytes  "WCGR"
//          4   version    u16      = 1
//          6   cols       u16      = COLS
//          8   rows       u16      = ROWS
//         10   fieldCount u16
//         12   field table, fieldCount × 32 bytes:
//                name       24 bytes ASCII, zero-padded
//                type       u8   (see FIELD_TYPE)
//                pad        3 bytes
//                byteOffset u32  from file start, 4-byte aligned
//   then each field's data: HEX_COUNT elements of its type, index = row * COLS + col.
//
// Several files may be loaded; their field tables merge (a name may appear in only one file).

import { COLS, HEX_COUNT, ROWS } from './grid.ts';

export const GRID_MAGIC = 'WCGR';
export const GRID_VERSION = 1;
const HEADER_BYTES = 12;
const FIELD_ENTRY_BYTES = 32;
const FIELD_NAME_BYTES = 24;

export const FIELD_TYPE = { u8: 1, i8: 2, u16: 3, i16: 4, u32: 5, f32: 6 } as const;
export type FieldTypeName = keyof typeof FIELD_TYPE;

export type FieldArray = Uint8Array | Int8Array | Uint16Array | Int16Array | Uint32Array | Float32Array;

const CTOR: Record<number, (buf: ArrayBuffer, offset: number) => FieldArray> = {
  [FIELD_TYPE.u8]: (b, o) => new Uint8Array(b, o, HEX_COUNT),
  [FIELD_TYPE.i8]: (b, o) => new Int8Array(b, o, HEX_COUNT),
  [FIELD_TYPE.u16]: (b, o) => new Uint16Array(b, o, HEX_COUNT),
  [FIELD_TYPE.i16]: (b, o) => new Int16Array(b, o, HEX_COUNT),
  [FIELD_TYPE.u32]: (b, o) => new Uint32Array(b, o, HEX_COUNT),
  [FIELD_TYPE.f32]: (b, o) => new Float32Array(b, o, HEX_COUNT),
};

/** Parse one grid file into named typed-array views (no copy). */
export function readGridFile(buf: ArrayBuffer): Map<string, FieldArray> {
  const view = new DataView(buf);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== GRID_MAGIC) {
    throw new Error(`grid file: bad magic ${JSON.stringify(magic)}`);
  }

  const version = view.getUint16(4, true);
  const cols = view.getUint16(6, true);
  const rows = view.getUint16(8, true);
  if (version !== GRID_VERSION || cols !== COLS || rows !== ROWS) {
    throw new Error(`grid file: version ${version} ${cols}x${rows}, expected ${GRID_VERSION} ${COLS}x${ROWS}`);
  }

  const fields = new Map<string, FieldArray>();
  const count = view.getUint16(10, true);
  for (let i = 0; i < count; i++) {
    const at = HEADER_BYTES + i * FIELD_ENTRY_BYTES;
    const name = new TextDecoder().decode(new Uint8Array(buf, at, FIELD_NAME_BYTES)).replace(/\0+$/, '');
    const type = view.getUint8(at + FIELD_NAME_BYTES);
    const offset = view.getUint32(at + FIELD_NAME_BYTES + 4, true);
    const make = CTOR[type];
    if (!make) {
      throw new Error(`grid file: field ${name} has unknown type ${type}`);
    }
    fields.set(name, make(buf, offset));
  }
  return fields;
}
